import React, { useCallback, useEffect, useState } from 'react';
import { url, authFetch } from './apiClient';
import { formatDateTime } from './dateFormat';
import { monthLabel } from './dashboardStats';
import './Dashboard.css';

// The owner's own notes, one per room per month (e.g. cash handed over, what's
// still to adjust), with what the tenant paid extra (positive balance) or still
// owes (negative balance). Only the admin sees these.

const thisMonth = () => new Date().toISOString().slice(0, 7);
const EMPTY = { note: '', positiveBalance: '', negativeBalance: '' };
const toDraft = (n) => ({
  note: n.note || '',
  positiveBalance: n.positiveBalance ? String(n.positiveBalance) : '',
  negativeBalance: n.negativeBalance ? String(n.negativeBalance) : '',
});
const sameDraft = (a, b) => a.note.trim() === b.note.trim()
  && Number(a.positiveBalance || 0) === Number(b.positiveBalance || 0)
  && Number(a.negativeBalance || 0) === Number(b.negativeBalance || 0);
const rupees = (v) => `₹${Number(v || 0).toLocaleString('en-IN')}`;
const inputStyle = { width: '100%', boxSizing: 'border-box', padding: '8px 10px', border: '1px solid var(--line-strong)',
  borderRadius: 8, fontSize: 15, fontFamily: 'inherit' };

export default function AdminRoomNotes() {
  const [month, setMonth] = useState(thisMonth());
  const [rooms, setRooms] = useState([]);
  const [saved, setSaved] = useState({});   // what the server has: { room: { note, positiveBalance, negativeBalance, updatedAt } }
  const [drafts, setDrafts] = useState({}); // what's in the boxes: { room: { note, positiveBalance, negativeBalance } }
  const [savingRoom, setSavingRoom] = useState(null);
  const [picked, setPicked] = useState('');
  // Rooms the owner picked to write a note for this month that haven't been saved yet, newest first.
  const [added, setAdded] = useState([]);
  const [error, setError] = useState('');

  useEffect(() => {
    authFetch(url.adminUsersAll())
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error('Could not load rooms'))))
      .then((users) => setRooms(users.filter((u) => u.role === 'TENANT')
        .sort((a, b) => a.username.localeCompare(b.username, undefined, { numeric: true, sensitivity: 'base' }))))
      .catch((e) => setError(e.message));
  }, []);

  const loadNotes = useCallback(async () => {
    setError('');
    try {
      const res = await authFetch(url.adminNotes(month));
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Could not load notes');
      setSaved(data);
      setDrafts(Object.fromEntries(Object.entries(data).map(([room, n]) => [room, toDraft(n)])));
    } catch (e) {
      setError(e.message);
    }
  }, [month]);

  useEffect(() => { setAdded([]); if (month) loadNotes(); }, [month, loadNotes]);

  const addRoom = () => {
    if (!picked) return;
    setAdded((a) => [picked, ...a]);
    setPicked('');
  };

  const save = async (room, override) => {
    setSavingRoom(room);
    try {
      const res = await authFetch(url.adminNoteSave(room, month), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(override ?? drafts[room] ?? EMPTY),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Could not save');
      setSaved((s) => {
        const next = { ...s };
        if (data.updatedAt) next[room] = data; else delete next[room];
        return next;
      });
      setDrafts((d) => ({ ...d, [room]: toDraft(data) }));
      setAdded((a) => a.filter((x) => x !== room));
    } catch (e) {
      alert(e.message);
    } finally {
      setSavingRoom(null);
    }
  };

  const removeNote = (room) => {
    if (!saved[room]) {
      setAdded((a) => a.filter((x) => x !== room));
      setDrafts((d) => ({ ...d, [room]: EMPTY }));
      return;
    }
    if (!window.confirm(`Delete the note for ${room}?`)) return;
    setDrafts((d) => ({ ...d, [room]: EMPTY }));
    save(room, EMPTY);
  };

  const setField = (room, field, value) =>
    setDrafts((d) => ({ ...d, [room]: { ...(d[room] || EMPTY), [field]: value } }));

  // Rooms just picked to add a note go on top (newest first), then rooms that
  // already have a note this month.
  const byName = Object.fromEntries(rooms.map((r) => [r.username, r]));
  const visible = [...added, ...rooms.map((r) => r.username).filter((u) => saved[u] && !added.includes(u))]
    .map((u) => byName[u]).filter(Boolean);
  const shown = new Set(visible.map((r) => r.username));
  const totals = Object.values(saved).reduce((t, n) => ({
    positive: t.positive + (n.positiveBalance || 0), negative: t.negative + (n.negativeBalance || 0),
  }), { positive: 0, negative: 0 });

  return (
    <div className="dash">
      <div className="dash-head">
        <div>
          <h2 className="dash-title">Notes</h2>
          <p className="dash-sub">Pick a room and write a note for this month. Enter a positive balance if the tenant paid extra, or a negative balance if they did not pay in full. Only you can see these.</p>
        </div>
        <div className="dash-actions">
          <label className="field-inline">
            <span>Month</span>
            <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} />
          </label>
        </div>
      </div>

      {error && <div className="alert alert--danger">{error}</div>}

      <div className="panel">
        <div className="panel__head" style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
          <strong>{monthLabel(month)}</strong>
          <span style={{ fontSize: 14 }}>
            <span style={{ color: 'var(--success, #1a7f37)' }}>Positive: {rupees(totals.positive)}</span>
            {' · '}
            <span style={{ color: 'var(--danger, #c62828)' }}>Negative: {rupees(totals.negative)}</span>
          </span>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
          <select value={picked} onChange={(e) => setPicked(e.target.value)}
            style={{ padding: '8px 10px', border: '1px solid var(--line-strong)', borderRadius: 8, fontSize: 15, background: '#fff', color: 'var(--ink)' }}>
            <option value="">Choose a room…</option>
            {rooms.filter((r) => !shown.has(r.username)).map((r) => (
              <option key={r.id} value={r.username}>{r.username}{r.fullName ? ` – ${r.fullName}` : ''}</option>
            ))}
          </select>
          <button type="button" className="btn btn--primary" onClick={addRoom} disabled={!picked}>Add note</button>
        </div>
        {visible.length === 0 && <p className="muted">No notes for this month yet.</p>}
        {visible.map((r) => {
          const draft = drafts[r.username] || EMPTY;
          const unchanged = sameDraft(draft, saved[r.username] ? toDraft(saved[r.username]) : EMPTY);
          return (
            <div key={r.id} style={{ padding: '12px 0', borderTop: '1px solid var(--line)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8, marginBottom: 6 }}>
                <span><strong>{r.username}</strong>{r.fullName && <span className="muted"> – {r.fullName}</span>}</span>
                {saved[r.username] && (
                  <span className="muted" style={{ fontSize: 12 }}>Saved {formatDateTime(saved[r.username].updatedAt)}</span>
                )}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 8, marginBottom: 6 }}>
                <label style={{ fontSize: 13 }}>
                  <span style={{ color: 'var(--success, #1a7f37)' }}>Positive balance (paid extra) ₹</span>
                  <input type="number" min="0" step="any" inputMode="decimal" value={draft.positiveBalance} placeholder="0"
                    onChange={(e) => setField(r.username, 'positiveBalance', e.target.value)} style={inputStyle} />
                </label>
                <label style={{ fontSize: 13 }}>
                  <span style={{ color: 'var(--danger, #c62828)' }}>Negative balance (not paid) ₹</span>
                  <input type="number" min="0" step="any" inputMode="decimal" value={draft.negativeBalance} placeholder="0"
                    onChange={(e) => setField(r.username, 'negativeBalance', e.target.value)} style={inputStyle} />
                </label>
              </div>
              <textarea
                value={draft.note}
                onChange={(e) => setField(r.username, 'note', e.target.value)}
                rows={2}
                maxLength={5000}
                placeholder="Type a note…"
                style={{ ...inputStyle, resize: 'vertical' }}
              />
              <div style={{ marginTop: 6, display: 'flex', gap: 8 }}>
                <button type="button" className="btn btn--primary" style={{ padding: '6px 14px' }}
                  onClick={() => save(r.username)} disabled={unchanged || savingRoom === r.username}>
                  {savingRoom === r.username ? 'Saving…' : unchanged ? 'Saved' : 'Save'}
                </button>
                <button type="button" className="btn btn--ghost" style={{ padding: '6px 14px' }} disabled={savingRoom === r.username}
                  onClick={() => removeNote(r.username)}>
                  {saved[r.username] ? 'Delete' : 'Cancel'}
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
